import * as _ from 'lodash'
import {IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_PolicyRow,
    WB_PolicySalesAreaRow,
    WB_PolicyProcedureRow,
    WB_PolicyCubeRow,
    WB_PolicyAreaRow,
    WB_ProductConditionTypeRow,
    WB_PolicyTermRow,
    WB_PolicyTermTypeRow,
    WB_PolicyCubeTermRow,
    WB_PolicySpendTypeTermRRow
} from "../PA.Core/PAdbSchemaRows";
import {
    esWB_Policy,
    esWB_Policy_SalesArea,
    esWB_SalesArea,
    esWB_SalesOrg,
    esWB_Policy_Procedure,
    esWB_MasterData_Set,
    esWB_PolicyArea,
    esWB_Period_Company_Relationship,
    esWB_Policy_Cube_TermR,
    esWB_Policy_Cube,
    esWB_Product_Condition_Type, esWB_Policy_Term, esWB_Policy_Term_Type, esWB_Policy_SpendType_TermR,
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";

import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {PolicyBOValidations} from "./PolicyBO.Validations";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {BehaviorSubject} from "rxjs";
import {PASearch} from "../PA.Core/PASearch";
import { PolicyBOAudit } from './PolicyBO.Audit';
import { ExecutionSerializer } from "../PA.Core/Basic/ExecutionSerializer";

export interface IPolicyProcedurePropertyChangeEvent {
    PolicyBO: PolicyBO,
    Row: WB_PolicyProcedureRow,
    PropertyChanged: string,
    PropertyValue: any
}

export class PolicyBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Policy: WB_PolicyRow;

    validityRange: { Start?: string, End?: string };

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Policy'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_Policy = null;
        this.validityRange = {}

        this.IHaveSalesAreas_init()
        this.IHavePolicyTerms_init()
        this.IHaveProcedure_init()
        this.IHaveCube_init()
    }

    get WB_Policy(): WB_PolicyRow {
        return this.drWB_Policy;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;

        if (self.bIsLoaded)
            return Promise.resolve(self)

        self.LoadProgressProvider.start("Creating Policy");
        let rv = self._OnLoadComplete = (async () => {
            try {

                self.LoadOptions = options || {};
                if ('dbContext' in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (_.isEmpty(this.dbContext)) {
                    let sError = "Unable to determine dbContext for new Policy.";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    throw new Error(sError);
                }

                if (!('metaTypeId' in self.LoadOptions)) {
                    let sError = "Policy Area ID not specified in create options";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    throw new Error(sError);
                }

                let whereClause: any = {where: {POLICYAREA_ID: self.LoadOptions.metaTypeId, DELETED_IND: {neq: 1} } }
                let policyAreaRow: WB_PolicyAreaRow;

                // Create record using dbContext; get policy area row from create options
                let policyAreas: WB_PolicyAreaRow[] = <WB_PolicyAreaRow[]> await self.dbContext.findAll(esWB_PolicyArea, whereClause)

                if (_.isEmpty(policyAreas)) {
                    var sError = "Policy Area ID not specified in create options";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    throw new Error(sError);
                }

                policyAreaRow = _.first(policyAreas)

                let newPolicyRow: WB_PolicyRow = <WB_PolicyRow> await self.dbContext.createRecordWithKey(esWB_Policy)

                newPolicyRow.setDefaults()
                newPolicyRow.cif.POLICY_NAME = self.LoadOptions.sName
                newPolicyRow.cif.WB_MDSET_ID = self.LoadOptions.wbMasterDataSetId || 0
                newPolicyRow.cif.WB_POLICYAREA_ID = self.LoadOptions.wbMetaTypeId || 0
                newPolicyRow.cif.WB_SALESORG_ID = self.LoadOptions.wbSalesOrgId || 0
                newPolicyRow.cif.WB_POLICYAREA_ID = policyAreaRow.cif.WB_POLICYAREA_ID
                self.drWB_Policy = newPolicyRow;

                self.dbContext.add(esWB_Policy, newPolicyRow)
                self.Identity.setAll(self.WB_Policy.cif.WB_POLICY_ID, self.WB_Policy.cif.POLICY_ID, self.WB_Policy.cif.POLICY_NAME);
                self.Identity.listenToRecordUpdates(self.WB_Policy, 'WB_POLICY_ID', 'POLICY_ID', 'POLICY_NAME')
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                // self.Identity.enableRecordWriteBack(self.WB_Policy, 'POLICY_ID', 'POLICY_NAME')

                await self._AfterBoLoadCompleteInternal()

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return (self);
            } catch (err) {
                self.LoadProgressProvider.exception(err)
                throw new Error(err)
            }

        })();

        return rv
    }

    Load(anId: number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Policy already loaded.");
                self._OnLoadComplete = null;
                resolve(self);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Policy.")
                return;
            }


            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                let createOptions = {
                    dbContext: self.dbContext,
                    standardConditions: {
                        [esWB_Policy]: {DELETED_IND: {neq: '1'}},
                        [esWB_Policy_Procedure]: {DELETED_IND: {neq: '1'}},
                        [esWB_Policy_Cube]: {DELETED_IND: {neq: '1'}},
                        [esWB_Policy_Cube_TermR]: {DELETED_IND: {neq: '1'}},
                        [esWB_PolicyArea]: {DELETED_IND: {neq: '1'}},
                        [esWB_Policy_SalesArea]: {DELETED_IND: {neq: '1'}}
                    },
                    dctIncludeBelongsToNamedRelations: {
                        [esWB_Policy_Procedure]: [esWB_Product_Condition_Type]
                    }
                }
                recordResolver = PolicyBO.createRecordResolver(createOptions)
                recordResolverLoadPromise = PolicyBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let aPolicyRow: any = recordResolver.get(esWB_Policy, anId)

                    if (_.isEmpty(aPolicyRow)) {
                        error("Policy not found");
                        return;
                    }

                    self.drWB_Policy = aPolicyRow;
                    self.Identity.setAll(self.WB_Policy.cif.WB_POLICY_ID, self.WB_Policy.cif.POLICY_ID, self.WB_Policy.cif.POLICY_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_Policy, 'WB_POLICY_ID', 'POLICY_ID', 'POLICY_NAME')
                    self.Identity.enableRecordWriteBack(self.WB_Policy, 'POLICY_ID', 'POLICY_NAME')
                    return self._AfterBoLoadCompleteInternal()
                })
                .then(() => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function (err: any) {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }


    async getFiscalYearRange(): Promise<void> {
        const self = this;

        if (!_.isEmpty(self.validityRange))
            return

        try {
            let companies = await self.dbContext.findAll(esWB_SalesOrg, {
                where: {
                    DELETED_IND: {neq: '1'},
                    WB_SALESORG_ID: self.WB_Policy.WB_SalesOrgRow.cif.WB_SALESORG_ID
                }
            })
            let restrictedYear = await self.dbContext.findAll(esWB_Period_Company_Relationship, {
                where: {
                    DELETED_IND: {neq: '1'},
                    WB_COMPANY_ID: {inq: _.map(companies, 'WB_COMPANY_ID')}
                }
            })
            let fiscalYears = await PASearch.find("vUI_Period_Search", {
                    PERIOD_TYPE_ID: 'Year',
                    WB_MDSET_ID: self.WB_Policy.cif.WB_MDSET_ID,
                    WB_PERIOD_ID: {inq: _.map(restrictedYear, 'WB_PERIOD_ID')}
                },
                {
                    PERIOD_TYPE_ID: {type: "string"},
                    WB_MDSET_ID: {type: "int"},
                    WB_PERIOD_ID: {type: "int"}
                }, ['PERIOD_START_DT', 'PERIOD_END_DT'], 0, [])


            self.validityRange.Start = _.min(_.map(fiscalYears, 'PERIOD_START_DT'))
            self.validityRange.End = _.max(_.map(fiscalYears, 'PERIOD_END_DT'))
            return
        } catch (e) {
            console.log("Loading validity date range error:" + e)
        }

        return

    }


    private async _AfterBoLoadCompleteInternal(): Promise<void> {
        const self = this;

        self.LoadProgressProvider.setProgressMessage('PolicyBO.PostLoad', 'Processing Loaded Data');

        self.WB_Policy.WB_MDSetRow = await self.dbContext.find(esWB_MasterData_Set, self.WB_Policy.cif.WB_MDSET_ID)
        self.WB_Policy.WB_SalesOrgRow = await self.dbContext.find(esWB_SalesOrg, self.WB_Policy.cif.WB_SALESORG_ID)
        self.WB_Policy.WB_PolicyAreaRow = await self.dbContext.find(esWB_PolicyArea, self.WB_Policy.cif.WB_POLICYAREA_ID)

        await self.getFiscalYearRange()
        self.WB_Policy.cif.EFFECTIVE_START_DT = _.isEmpty(self.WB_Policy.cif.EFFECTIVE_START_DT) ? self.validityRange.Start : self.WB_Policy.cif.EFFECTIVE_START_DT
        self.WB_Policy.cif.EFFECTIVE_END_DT = _.isEmpty(self.WB_Policy.cif.EFFECTIVE_END_DT) ? self.validityRange.End : self.WB_Policy.cif.EFFECTIVE_END_DT
        await self.IHaveProcedure_onLoadComplete()
        await self.IHaveCube_onLoadComplete()
        await self.OutputConditionsSetup_onLoadComplete()

    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;

        try {
            self.ValidateMessageProvider.clearMessages();
            self.ValidateProgressProvider.start("Validating " + self.WB_Policy.cif.POLICY_NAME + "(" + self.WB_Policy.cif.POLICY_ID + ")");

            // Does no validations yet
            self.ValidateOptions = options || {};

            if (_.isEmpty(self.dbContext)) {
                let sError = "Unable to determine dbContext for to Validate Policy.";
                self.ValidateProgressProvider.exception(sError);
                throw new Error(sError)
            }

            await PolicyBOValidations.ValidateFields(self)


            self._bIsValidated = !(self.ValidateMessageProvider.countMessages(MessageType.Error) > 0);

            self.ValidateProgressProvider.finish();

            return self

        } catch (err: any) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
            return self

        }

    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        opts.dctExcludeFields = opts.dctExcludeFields || {}
        opts.dctExcludeFields[esWB_Policy] = []
        opts.bIgnoreStandardFields = true
        opts.dctIgnoreChanges = opts.dctIgnoreChanges || {}

        let changeSet = DefaultChanges(this, this.WB_Policy, opts)
        return changeSet
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;

        try {

            self.SaveProgressProvider.start("Saving " + self.WB_Policy.cif.POLICY_NAME + "(" + self.WB_Policy.cif.POLICY_ID + ")");

            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Policy.";
                self.SaveProgressProvider.exception(sError);
                throw new Error(sError)
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }


            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

           await PolicyBOAudit.PolicyBOAudit(self)

            if (self.Changes().bHasChanges)
                self.WB_Policy.cif.MODIFIED_DTTS = DateUtils.convertToSAPBWTs(new Date())

            let tasks: Promise<any> [] = [];
            tasks.push(saveFunction(esWB_Policy, self.drWB_Policy, {}))
            tasks.push(saveFunction(esWB_Policy_Term, self.drWB_Policy.WB_PolicyTermRows, {}))
            tasks.push(saveFunction(esWB_Policy_Procedure, self.drWB_Policy.WB_PolicyProcedureRows, {}))
            tasks.push(saveFunction(esWB_Policy_SalesArea, self.drWB_Policy.WB_PolicySalesAreaRows, {}))
            tasks.push(saveFunction(esWB_Policy_Cube, self.drWB_Policy.WB_PolicyCubeRows, {}))
            tasks.push(saveFunction(esWB_Policy_SpendType_TermR, self.drWB_Policy.WB_PolicySpendTypeTermRRows, {}))
            let cubeTerms: WB_PolicyCubeTermRow[] = _.flatMap(_.map(self.drWB_Policy.WB_PolicyCubeRows, (aRow: WB_PolicyCubeRow) => {
                return aRow.WB_PolicyCubeTermRows
            }))
            tasks.push(saveFunction(esWB_Policy_Cube_TermR, cubeTerms, {}))
            await Promise.all(tasks)


            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
            return self

        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }
        return self

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }


    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        let self = this;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + self.WB_Policy.cif.POLICY_NAME + "(" + self.WB_Policy.cif.WB_POLICY_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Policy

            // 2. If Valid, mark all the records for deletion.
            self.WB_Policy.markDeleted()
            _.forEach(self.WB_Policy.WB_PolicySalesAreaRows, aRow => {
                if (aRow.isActive)
                    aRow.markDeleted()
            })


            _.forEach(self.WB_Policy.WB_PolicyTermRows, aRow => {
                if (aRow.isActive)
                    aRow.markDeleted()
            })

            _.forEach(self.WB_Policy.WB_PolicyProcedureRows, aRow => {
                if (aRow.isActive)
                    aRow.markDeleted()
            })


            _.forEach(self.WB_Policy.WB_PolicyCubeRows, aRow => {
                if (aRow.isActive) {
                    aRow.markDeleted()
                    _.forEach(aRow.WB_PolicyCubeTermRows, row => {
                        if (row.isActive)
                            row.markDeleted()
                    })
                }
            })

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const policy_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        }

        if ('standardConditions' in opts) {
            policy_options.standardConditions = opts.standardConditions
        }

        if ('dctIncludeBelongsToNamedRelations' in opts) {
            policy_options.dctIncludeBelongsToNamedRelations = opts.dctIncludeBelongsToNamedRelations
        }

        const aRecResolver = new ESRecordResolver(dbContext, policy_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Policy, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }


    // Sales Area changes
    _salesAreasChanged: BehaviorSubject<PolicyBO>

    IHaveSalesAreas_init() {
        this._salesAreasChanged = new BehaviorSubject<PolicyBO>(this)
    }

    get SalesAreasChanged(): BehaviorSubject<PolicyBO> {
        return this._salesAreasChanged
    }

    get bHasSalesAreasAdded(): boolean {
        if (_.filter(this.WB_Policy.WB_PolicySalesAreaRows, aRow => aRow.isActive).length)
            return true
        else
            return false
    }

    addPolicySalesArea(wbSalesAreaIds: number[]): Promise<WB_PolicySalesAreaRow> {
        const self = this;
        return new Promise<WB_PolicySalesAreaRow>((resolve, error) => {
            _.forEach(wbSalesAreaIds, wbSalesAreaId => {
                (async () => {
                    try {
                        let found = undefined
                        _.forEach(self.WB_Policy.WB_PolicySalesAreaRows, aRow => {
                            if (aRow.isActive && aRow.cif.WB_SALESAREA_ID == wbSalesAreaId) {
                                found = aRow
                                return
                            }
                        })
                        if (found) {
                            resolve(found)
                            return
                        }

                        const aRecResolver = new ESRecordResolver(self.dbContext, {})
                        let saleAreaRow = _.first(await aRecResolver.findAllWithRelationships(esWB_SalesArea, [wbSalesAreaId]))
                        let aNewRow: any = <WB_PolicySalesAreaRow>await self.dbContext.createRecordWithKey(esWB_Policy_SalesArea)
                        aNewRow.setDefaults()
                        aNewRow.cif.WB_POLICY_ID = self.WB_Policy.cif.WB_POLICY_ID
                        aNewRow.cif.WB_SALESAREA_ID = wbSalesAreaId
                        aNewRow.WB_SalesAreaRow = saleAreaRow
                        aNewRow.WB_PolicyRow = self.WB_Policy
                        self.dbContext.add(esWB_Policy_SalesArea, aNewRow)
                        self.SalesAreasChanged.next(self)
                        resolve(aNewRow)
                    } catch (err) {
                        error(err)
                    }
                })()
            })
        })
    }

    removePolicySalesArea(wbPolicySalesAreaId: number): Promise<void> {
        let self = this

        return new Promise<void>((resolve, error) => {
            _.forEach(self.WB_Policy.WB_PolicySalesAreaRows, aRow => {
                if (aRow.isActive && aRow.cif.WB_POLICYSAR_ID == wbPolicySalesAreaId) {
                    aRow.markDeleted()
                }
            })
            self.SalesAreasChanged.next(self)
            resolve()
        })
    }


    // Output Conditions
    private async OutputConditionsSetup_onLoadComplete(): Promise<void> {
        const self = this

        // Find all the Condition Types for these Policy Cubes, and set up the output conditions keys
        let arrWbCubeIds = _.map(_.filter(self.WB_Policy.WB_PolicyCubeRows, (a) => a.isActive),
            (b) => b.cif.WB_POLICYCUBE_ID)

        if(_.isEmpty(arrWbCubeIds))
            return

        // Search for the Conditions
        let arrCnd = <WB_ProductConditionTypeRow[]> await self.dbContext.findAll(esWB_Product_Condition_Type,
            {where: {WB_POLICYCUBE_ID: {inq: arrWbCubeIds}
            }})

        let dctCndByCubeTermId: {[wbPolicyCubeId: number]: {[wbPolicyTermId: number]: WB_ProductConditionTypeRow}} = {}
        _.forEach(arrCnd, (cnd) => {
            let wbPolicyCubeId = cnd.cif.WB_POLICYCUBE_ID
            let wbPolicyTermId = cnd.cif.WB_POLICYTERM_ID

            if(!(wbPolicyCubeId in dctCndByCubeTermId))
                dctCndByCubeTermId[wbPolicyCubeId] = {}

            dctCndByCubeTermId[wbPolicyCubeId][wbPolicyTermId] = cnd
        })


        _.forEach(self.WB_Policy.WB_PolicyCubeRows, (cube) => {
            let wbPolicyCubeId = cube.cif.WB_POLICYCUBE_ID
            if(!(wbPolicyCubeId in dctCndByCubeTermId))
                return

            _.forEach(cube.WB_PolicyCubeTermRows, (row) => {
                if(!row.isActive)
                    return

                let wbPolicyTermId = row.cif.WB_POLICYTERM_ID


                let cnd = dctCndByCubeTermId[wbPolicyCubeId][wbPolicyTermId]
                if(_.isEmpty(cnd)){
                    row.cif.PRD_CNDM_ID = 0
                } else {
                    row.cif.PRD_CNDM_ID = cnd.cif.PRD_CNDM_ID
                    row.WB_ProductConditionTypeRow = cnd
                }
            })

        })
    }

    // Procedure changes
    _policyTermCollectionChanged: BehaviorSubject<PolicyBO>

    IHavePolicyTerms_init() {
        const self = this
        self._policyTermCollectionChanged = new BehaviorSubject<PolicyBO>(this)
    }

    get PolicyTermCollectionChanged() { return this._policyTermCollectionChanged }

    async addPolicyTerm(wbPolicyTermMId: number): Promise<WB_PolicyTermRow> {
        const self = this

        let typeRow = <WB_PolicyTermTypeRow> await self.dbContext.find(esWB_Policy_Term_Type, wbPolicyTermMId)
        if(_.isEmpty(typeRow))
            return undefined

        let newRow = <WB_PolicyTermRow> await self.dbContext.createRecordWithKey(esWB_Policy_Term, {})
        newRow.cif.WB_POLICY_ID = self.WB_Policy.cif.WB_POLICY_ID
        newRow.WB_PolicyRow = self.WB_Policy
        newRow.cif.WB_POLICY_TERMM_ID = typeRow.cif.WB_POLICYTERMM_ID
        newRow.WB_PolicyTermTypeRow = typeRow
        self.dbContext.add(esWB_Policy_Term, newRow)
        self.PolicyTermCollectionChanged.next(self)
        return newRow
    }

    async removePolicyTerm(wbPolicyTermId: number): Promise<void> {
        const self = this
        _.forEach(self.WB_Policy.WB_PolicyTermRows, (a) => {
            if(a.cif.WB_POLICYTERM_ID == wbPolicyTermId)
                a.markDeleted()
        })

        self.PolicyTermCollectionChanged.next(self)
    }

    // Procedure changes
    _procedureCollectionChanged: BehaviorSubject<PolicyBO>
    _procedurePropertyChanged: BehaviorSubject<IPolicyProcedurePropertyChangeEvent>

    IHaveProcedure_init() {
        this._procedureCollectionChanged = new BehaviorSubject<PolicyBO>(this)
        this._procedurePropertyChanged = new BehaviorSubject<IPolicyProcedurePropertyChangeEvent>(undefined)
    }

    private async IHaveProcedure_onLoadComplete(): Promise<void> {
        const self = this

        _.forEach(self.WB_Policy.WB_PolicyProcedureRows, (aRow) => {
            aRow.setupOnChangeEvents([], self.PolicyProcedureRecordChange.bind(self), self)
        })
    }

    get ProcedureCollectionChanged(): BehaviorSubject<PolicyBO> {
        return this._procedureCollectionChanged
    }

    get ProcedurePropertyChanged(): BehaviorSubject<IPolicyProcedurePropertyChangeEvent> {
        return this._procedurePropertyChanged
    }

    private PolicyProcedureRecordChange(record: WB_PolicyProcedureRow, propertyName: string, propertyValue: any, options: PolicyBO) {
        if (propertyName == "LINE_ID")
            this._procedurePropertyChanged.next({
                PolicyBO: options,
                Row: record,
                PropertyChanged: propertyName,
                PropertyValue: propertyValue
            })
    }


    async addPolicyProcedure(wbPolicyTermId: number): Promise<WB_PolicyProcedureRow> {
        const self = this;

        let policyTerm: WB_PolicyTermRow = undefined
        _.forEach(self.WB_Policy.WB_PolicyTermRows, (a) => {
          if(a.cif.WB_POLICYTERM_ID == wbPolicyTermId){
              policyTerm = a
              return false
          }
        })

        let aNewRow: WB_PolicyProcedureRow = <WB_PolicyProcedureRow> await self.dbContext.createRecordWithKey(esWB_Policy_Procedure);
        aNewRow.setDefaults();
        aNewRow.cif.WB_POLICY_ID = self.WB_Policy.cif.WB_POLICY_ID;
        aNewRow.WB_PolicyRow = self.WB_Policy;
        aNewRow.cif.WB_POLICYTERM_ID = policyTerm.cif.WB_POLICYTERM_ID
        aNewRow.WB_PolicyTermRow = policyTerm
        aNewRow.cif.LINE_ID = self.getNextID("Procedure");
        aNewRow.setupOnChangeEvents([], self.PolicyProcedureRecordChange.bind(self), self);
        self.dbContext.add(esWB_Policy_Procedure, aNewRow);
        self.ProcedureCollectionChanged.next(self);
        return aNewRow;
    }

    async removePolicyProcedure(wbPolicyProcedureId: number): Promise<void> {
        let self = this;

        _.forEach(self.WB_Policy.WB_PolicyProcedureRows, aRow => {
            if (aRow.isActive && aRow.cif.WB_POLICYPLR_ID == wbPolicyProcedureId) {
                aRow.markDeleted();
            }
        });
        self.ProcedureCollectionChanged.next(self);
    }

    private getNextID(sType: string, sCubeType?: string): string {
        let self = this
        let rows: WB_PolicyProcedureRow[] | WB_PolicyCubeRow[] = []
        let field: string = ""
        if (sType == 'Procedure') {
            rows = _.filter(self.WB_Policy.WB_PolicyProcedureRows, aRow => aRow.isActive)
            field = "LINE_ID"
        }

        if (sType == 'Cube') {
            rows = _.filter(self.WB_Policy.WB_PolicyCubeRows, aRow => aRow.isActive)
            field = "CUBE_ID"
        }
        let maxLine: number = _.max(_.map(rows, (aRow: any) => {
            return Number(aRow.cif[field])
        }))
        if (_.isUndefined(maxLine))
            maxLine = 0
        maxLine++

        return maxLine.toString()
    }

    // Cube changes
    _cubeCollectionChanged: BehaviorSubject<PolicyBO>
    _cubeTermCollectionChanged: BehaviorSubject<PolicyBO>
    _qsSyncCubeTerms: ExecutionSerializer

    IHaveCube_init() {
        const self = this
        this._cubeCollectionChanged = new BehaviorSubject<PolicyBO>(this)
        this._cubeTermCollectionChanged = new BehaviorSubject<PolicyBO>(this)
        this._qsSyncCubeTerms = new ExecutionSerializer(this,  this._syncPolicyCubeTerms.bind(this), [], 500)

        this._cubeCollectionChanged.subscribe((a) => {
            self.SyncPolicyCubeTerms()
        })
    }

    private async IHaveCube_onLoadComplete(): Promise<void> {
        await this._syncPolicyCubeTerms()
    }
        get CubeCollectionChanged(): BehaviorSubject<PolicyBO> {
        return this._cubeCollectionChanged
    }

    get CubeTermCollectionChanged(): BehaviorSubject<PolicyBO> {
        return this._cubeTermCollectionChanged
    }


    async addPolicyCube(sCubeType: string): Promise<WB_PolicyCubeRow> {
        const self = this;

        let aNewRow: WB_PolicyCubeRow = <WB_PolicyCubeRow> await self.dbContext.createRecordWithKey(esWB_Policy_Cube);

        aNewRow.setDefaults();
        aNewRow.cif.WB_POLICY_ID = self.WB_Policy.cif.WB_POLICY_ID;
        aNewRow.WB_PolicyRow = self.WB_Policy;
        aNewRow.cif.CUBE_ID = self.getNextID("Cube", sCubeType);
        aNewRow.cif.CUBE_TYPE = sCubeType;

        self.dbContext.add(esWB_Policy_Cube, aNewRow);
        self.CubeCollectionChanged.next(self);
        return aNewRow

    }

    //---
    async removePolicyCube(wbPolicyCubeId: number, sCubeType: string): Promise<void> {
        let self = this

        _.forEach(self.WB_Policy.WB_PolicyCubeRows, aRow => {
            if (aRow.isActive && aRow.cif.WB_POLICYCUBE_ID == wbPolicyCubeId) {
                aRow.markDeleted()
                _.forEach(aRow.WB_PolicyCubeTermRows, row => {
                    if (row.isActive) {
                        row.markDeleted()
                    }
                })

            }
        })
        let idx = 0
        _.forEach(_.sortBy(_.filter(self.WB_Policy.WB_PolicyCubeRows, aRow => aRow.isActive), row => parseInt(row.cif.CUBE_ID, 10)), aRow => {
            idx++
            aRow.cif.CUBE_ID = idx.toString()
        })
        self.CubeCollectionChanged.next(self)
    }

     SyncPolicyCubeTerms(): void {
         this._qsSyncCubeTerms.Run()
     }

     async _syncPolicyCubeTerms(): Promise<void> {
        const self = this

         //--
         let arrPolicyTerms = _.filter(self.WB_Policy.WB_PolicyTermRows, (a) => a.isActive)
         let dctPolicyTerms = _.keyBy(arrPolicyTerms, (a) => a.cif.WB_POLICYTERM_ID)

         let cubes = _.filter(self.WB_Policy.WB_PolicyCubeRows, (a) => a.isActive)

         for(let aCube of cubes){
             let dctToAdd = _.clone(dctPolicyTerms)
             let cubeTerms = _.filter(aCube.WB_PolicyCubeTermRows, (a)=> a.isActive)
             for(let aCubeTerm of cubeTerms){
                 let wbCubeTermId = aCubeTerm.cif.WB_POLICYTERM_ID
                 if(wbCubeTermId in dctToAdd){
                     delete dctToAdd[wbCubeTermId]
                 } else {
                     aCubeTerm.markDeleted()
                 }
             }

             for(let aTerm of _.values(dctToAdd)){
                 await self.addPolicyCubeTerm(aCube, aTerm)
             }

         }

         self.CubeTermCollectionChanged.next(self)
    }

    async addPolicyCubeTerm(rowCube: WB_PolicyCubeRow, rowTerm: WB_PolicyTermRow): Promise<WB_PolicyCubeTermRow> {
        const self = this;

        let aNewRow: WB_PolicyCubeTermRow = <WB_PolicyCubeTermRow> await self.dbContext.createRecordWithKey(esWB_Policy_Cube_TermR);
        aNewRow.setDefaults();
        aNewRow.cif.WB_POLICYCUBE_ID = rowCube.cif.WB_POLICYCUBE_ID;
        aNewRow.WB_PolicyCubeRow = rowCube;
        aNewRow.cif.WB_POLICYTERM_ID = rowTerm.cif.WB_POLICYTERM_ID;
        aNewRow.WB_PolicyTermRow = rowTerm;
        self.dbContext.add(esWB_Policy_Cube_TermR, aNewRow);
        return aNewRow;
    }


    async addPolicySpendTypeTermR(): Promise<WB_PolicySpendTypeTermRRow> {
        const self = this

        let newRow = <WB_PolicySpendTypeTermRRow> await self.dbContext.createRecordWithKey(esWB_Policy_SpendType_TermR, {})
        newRow.cif.WB_POLICY_ID = self.WB_Policy.cif.WB_POLICY_ID
        newRow.WB_PolicyRow = self.WB_Policy

        self.dbContext.add(esWB_Policy_SpendType_TermR, newRow)
        return newRow
    }

    async removePolicySpendTypeTermR(wbPolicySpendTypeTermId: number): Promise<void> {
        const self = this
        _.forEach(self.WB_Policy.WB_PolicySpendTypeTermRRows, (a) => {
            if(a.cif.WB_POLICYSPENDTYPETERMR_ID == wbPolicySpendTypeTermId)
                a.markDeleted()
        })
    }

}

PAEnvironment.registerBusinessObjectClass(PolicyBO, "PolicyBO", "Policy")
